import {
  TNotificationType,
  TNotificationTypeName
} from '../../BottomNotificationProvider/types'
import { useCallback, useEffect, useState } from 'react'
import { setLoading } from '../../../redux/store/common/slice'
import { handleFilesErrors } from '../../../helper/common'
import {
  BTN_TXT,
  FIREBASE_STATUSES,
  TDocumentsErrors
} from '../../../constants'
import { useDispatch, useSelector } from 'react-redux'
import { useBottomNotificationsContext } from '../../BottomNotificationProvider/BottomNotificationsContextProvider'
import { getToken } from '../../../redux/store/notifications/getters'
import { usePolling } from '../../../hooks/usePolling'
import { routes } from '../../../router'
import { checkInvoicesFileWasUploaded, uploadInvoicesFile } from '../api'
import { TProps } from './InvoiceDataUpload'

export const useUpload = ({ onFinish }: TProps) => {
  const {
    actions: { openNotification, checkIfProcessIsRunning, onChangeNotification }
  } = useBottomNotificationsContext()

  const registrationId = useSelector(getToken)

  const { poll } = usePolling()

  const dispatch = useDispatch()

  const [uploadButtonDisabled, setUploadButtonDisabled] =
    useState<boolean>(false)

  const uploadFinishedCallback = useCallback(
    (data) => {
      onChangeNotification(
        {
          id: data?.uuid,
          type:
            data?.status === FIREBASE_STATUSES.COMPLETED
              ? TNotificationType.SUCCESS
              : TNotificationType.ERROR,
          messageProps:
            data?.status === FIREBASE_STATUSES.COMPLETED
              ? {
                  buttonText: BTN_TXT.SEE_INVOICES
                }
              : undefined
        },
        TNotificationTypeName.UPLOAD_INVOICES
      )
    },
    [onChangeNotification]
  )

  const disableUploadButton = () => {
    const uploadInProgress = checkIfProcessIsRunning(
      'upload-invoices',
      TNotificationTypeName.UPLOAD_INVOICES
    )
    setUploadButtonDisabled(uploadInProgress)
  }

  useEffect(() => {
    disableUploadButton()
    window.addEventListener('storage', disableUploadButton)
    return () => window.removeEventListener('storage', disableUploadButton)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUploadFile = async (files: File[], documentType: string) => {
    dispatch(setLoading(true))

    const formData = new FormData()
    formData.append(documentType, files[0], files[0].name)

    try {
      await uploadInvoicesFile({
        document_type: documentType,
        file: formData.get(documentType)
      }).then(openNotificationOnResponse)
    } catch (e) {
      handleFilesErrors(e as TDocumentsErrors)
    } finally {
      onFinish()
      dispatch(setLoading(false))
    }
  }

  const openNotificationOnResponse = useCallback(
    (response) => {
      openNotification(
        {
          id: response.data.uuid,
          type: TNotificationType.INFO,
          date: new Date().toISOString(),
          numberOfItems: 1,
          messageProps: {
            itemUuid: 'upload-invoices',
            redirectRoute: `${routes.invoiceData}/0`
          }
        },
        TNotificationTypeName.UPLOAD_INVOICES,
        true
      )
      if (!registrationId) {
        poll({
          request: async () => checkInvoicesFileWasUploaded(response.data.uuid),
          successCallback: uploadFinishedCallback
        })
      }
    },
    [openNotification, registrationId, poll, uploadFinishedCallback]
  )

  return {
    handleUploadFile,
    disableUploadButton: uploadButtonDisabled
  }
}
